import { useMemo } from 'react';

import OrderModalTimeline from 'src/features/product/product-bulk-orderings/ui/OrderModalTimeline';
import MarkdownContent from 'src/features/product/product-detail/ui/updates-tab/markdown-content/MarkdownContent';
import { getRfqMarkdownForDisplay } from 'src/features/product/product-detail/utils/extractProductDescription';
import { BulkOrdering } from 'src/shared/types/bulkOrdering';
import { RFQ } from 'src/shared/types/product';
import { Timeline } from 'src/shared/types/timeline';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { isImageMimeType } from 'src/shared/utils/fileTypeUtils';

import { Tab, Tabs } from 'src/shared/components/ui';
import { Typography } from 'src/shared/components/ui';

const getFileSource = (tags: string[]): string => {
  const sourceTag = tags.find((tag) => tag.startsWith('source:'));

  return sourceTag ? sourceTag.replace('source:', '') : 'other';
};

const SOURCE_LABELS: Record<string, string> = {
  user: 'Uploaded Files',
  reference: 'Reference Images',
  ai: 'AI Generated',
  rfq_extracted: 'Extracted from Document'
};

const SOURCE_ORDER = ['user', 'reference', 'ai', 'rfq_extracted'];

/** Fixed dates so the Cavela Direct “Order timeline” tab matches bulk-order modal layout. */
const CAVELA_DIRECT_TIMELINE_PREVIEW_ANCHOR = new Date();

const isoDaysFromAnchor = (dayOffset: number): string =>
  new Date(
    CAVELA_DIRECT_TIMELINE_PREVIEW_ANCHOR.getTime() + dayOffset * 86400000
  ).toISOString();

/**
 * Simulated order still in the Payment phase: `stage` is `paid` so the timeline
 * highlights the Payment group (requested + paid). Later phases are estimates only.
 * UI preview only (not real API data).
 */
const CAVELA_DIRECT_TIMELINE_PREVIEW_ORDER = {
  stage: 'requested',
  estimated_end_date: isoDaysFromAnchor(52),
  timeline: [
    {
      stage: 'requested',
      started_at: isoDaysFromAnchor(0),
      finished_at: isoDaysFromAnchor(1),
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(0),
      estimated_finished_at: isoDaysFromAnchor(2),
      timelineIndex: 0
    },
    {
      stage: 'paid',
      started_at: isoDaysFromAnchor(1),
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(1),
      estimated_finished_at: isoDaysFromAnchor(4),
      timelineIndex: 1
    },
    {
      stage: 'production-started',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(4),
      estimated_finished_at: isoDaysFromAnchor(12),
      timelineIndex: 2
    },
    {
      stage: 'production-completed',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(10),
      estimated_finished_at: isoDaysFromAnchor(18),
      timelineIndex: 3
    },
    {
      stage: 'qa-started',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(16),
      estimated_finished_at: isoDaysFromAnchor(24),
      timelineIndex: 4
    },
    {
      stage: 'qa-completed',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(22),
      estimated_finished_at: isoDaysFromAnchor(28),
      timelineIndex: 5
    },
    {
      stage: 'approved',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(26),
      estimated_finished_at: isoDaysFromAnchor(30),
      timelineIndex: 6
    },
    {
      stage: 'sent',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(28),
      estimated_finished_at: isoDaysFromAnchor(40),
      timelineIndex: 7
    },
    {
      stage: 'delivered',
      started_at: null,
      finished_at: null,
      client_notes: null,
      estimated_started_at: isoDaysFromAnchor(48),
      estimated_finished_at: isoDaysFromAnchor(52),
      timelineIndex: 8
    }
  ] satisfies Timeline[]
} as BulkOrdering;

export type CavelaDirectRfqTab = 'rfq' | 'timeline';

export interface CavelaDirectRfqTabsPanelProps {
  rfq: RFQ;
  activeTab: CavelaDirectRfqTab;
  onActiveTabChange: (tab: CavelaDirectRfqTab) => void;
  /** Drives timeline animations (e.g. drawer open vs inline visible tab). */
  timelineOpen: boolean;
  /** When true, tab list uses sticky positioning like the Cavela Direct slide-over. */
  stickyTabs?: boolean;
  className?: string;
}

/**
 * RFQ summary and order timeline tabs shared by the Cavela Direct slide-over and
 * inline surfaces (e.g. product quotes tab).
 */
const CavelaDirectRfqTabsPanel: React.FC<CavelaDirectRfqTabsPanelProps> = ({
  rfq,
  activeTab,
  onActiveTabChange,
  timelineOpen,
  stickyTabs = false,
  className
}) => {
  const filesBySource = useMemo(() => {
    const files: UploadedFile[] = (rfq.files ?? []) as UploadedFile[];
    const grouped: Record<string, UploadedFile[]> = {};

    for (const file of files) {
      if (file.tags.includes('visibility:hidden')) continue;

      const source = getFileSource(file.tags);

      if (!grouped[source]) {
        grouped[source] = [];
      }
      grouped[source].push(file);
    }

    return grouped;
  }, [rfq.files]);

  const sortedSourceKeys = useMemo(() => {
    const keys = Object.keys(filesBySource);

    return keys.sort((a, b) => {
      const indexA = SOURCE_ORDER.indexOf(a);
      const indexB = SOURCE_ORDER.indexOf(b);
      const orderA = indexA === -1 ? SOURCE_ORDER.length : indexA;
      const orderB = indexB === -1 ? SOURCE_ORDER.length : indexB;

      return orderA - orderB;
    });
  }, [filesBySource]);

  const hasFiles = sortedSourceKeys.length > 0;

  const cleanedMarkdown = useMemo(() => getRfqMarkdownForDisplay(rfq), [rfq]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'rfq':
        return (
          <div className="px-4 mobile:px-0">
            {cleanedMarkdown ? (
              <div className="prose prose-slate max-w-none mobile:ml-6">
                <MarkdownContent markdownString={cleanedMarkdown} />
              </div>
            ) : (
              <Typography variant="body-1" className="text-secondary-text">
                No RFQ description available.
              </Typography>
            )}

            {hasFiles && (
              <div className="mt-8 space-y-6">
                {sortedSourceKeys.map((source) => (
                  <div key={source}>
                    <Typography variant="body-1" weight="bold" className="mb-2">
                      {SOURCE_LABELS[source] ?? source}
                    </Typography>
                    <div className="flex flex-col gap-2">
                      {filesBySource[source].map((file) => {
                        const isImage = isImageMimeType(
                          file.content_type ?? ''
                        );

                        return (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white p-2"
                          >
                            {isImage ? (
                              <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-50">
                                <img
                                  src={file.url}
                                  alt={file.description || file.name}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                            <p className="min-w-0 truncate text-sm text-gray-700">
                              {file.description || file.name}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'timeline':
        return (
          <OrderModalTimeline
            order={CAVELA_DIRECT_TIMELINE_PREVIEW_ORDER}
            isOpen={timelineOpen}
          />
        );
    }
  };

  return (
    <div className={className}>
      <div
        className={
          stickyTabs
            ? 'sticky top-0 z-10 -mx-6 bg-transparent px-6 pb-4'
            : 'pb-4'
        }
      >
        <Tabs
          fullWidth
          selectedKey={activeTab}
          onSelectionChange={(key) =>
            onActiveTabChange(key as CavelaDirectRfqTab)
          }
        >
          <Tab key="rfq" title="RFQ summary" />
          {/* // bring back in 0.3 cavela direct release */}
          {/* <Tab key="timeline" title="Order timeline" /> */}
        </Tabs>
      </div>

      <div className="flex-1">{renderTabContent()}</div>
    </div>
  );
};

export default CavelaDirectRfqTabsPanel;
